import { html, css, LitElement } from "lit";
import { customElement } from "lit/decorators.js";
import { state } from "lit/decorators.js";
import { hermesClient } from "../hermes-singleton.js";
import { toSanitizedMarkdownHtml } from "../markdown.ts";
import { formatMs } from "../format.ts";

type ChatMessage = {
  id?: string;
  role: string;
  content: string;
  created_at?: number;
  label?: string;
};

@customElement("ead-chat-view")
export class EadChatView extends LitElement {
  static styles = css`
    :host { display: block; height: 100%; }
    .chat-container { display: flex; flex-direction: column; height: 100%; }
    .chat-header { padding: 16px 24px; border-bottom: 1px solid var(--border, #2e2f33); display: flex; justify-content: space-between; align-items: center; }
    .chat-header h2 { margin: 0; font-size: 16px; font-weight: 600; }
    .messages { flex: 1; overflow-y: auto; padding: 16px 24px; }
    .msg { margin-bottom: 16px; display: flex; gap: 12px; }
    .msg-avatar { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 14px; flex-shrink: 0; }
    .msg-avatar.user { background: #6ea8fe33; color: #6ea8fe; }
    .msg-avatar.assistant { background: #4caf5033; color: #4caf50; }
    .msg-avatar.system { background: #ff980033; color: #ff9800; }
    .msg-body { flex: 1; min-width: 0; }
    .msg-meta { font-size: 11px; color: var(--muted, #8b8d93); margin-bottom: 4px; }
    .msg-content { font-size: 14px; line-height: 1.6; color: var(--fg, #e4e5e7); }
    .msg-content p { margin: 0 0 8px; }
    .msg-content pre { background: rgba(0,0,0,0.3); padding: 12px; border-radius: 6px; overflow-x: auto; font-size: 12px; }
    .msg-content code { background: rgba(0,0,0,0.2); padding: 1px 4px; border-radius: 3px; font-size: 13px; }
    .input-bar { padding: 16px 24px; border-top: 1px solid var(--border, #2e2f33); display: flex; gap: 12px; position: relative; z-index: 100; pointer-events: auto; background: var(--bg-surface, #222326); }
    .input-bar input {
      flex: 1;
      padding: 12px 16px;
      border-radius: 20px;
      border: 1px solid var(--border, #2e2f33);
      background: var(--bg, #1a1b1e);
      color: var(--fg, #e4e5e7);
      font-size: 14px;
      pointer-events: auto !important;
      transition: border-color 0.2s;
    }
    .input-bar input:focus { outline: none; border-color: var(--accent, #6ea8fe); box-shadow: 0 0 0 2px rgba(110, 168, 254, 0.2); }
    .input-bar input:disabled { opacity: 0.5; cursor: not-allowed; pointer-events: none; }
    .btn { padding: 8px 16px; border-radius: 8px; border: 1px solid var(--border, #2e2f33); background: transparent; color: var(--fg, #e4e5e7); font-size: 13px; cursor: pointer; font-weight: 500; transition: all 0.2s; }
    .btn:hover { background: rgba(255,255,255,0.05); }
    .btn-new { background: var(--accent, #6ea8fe); color: white; border-color: var(--accent, #6ea8fe); }
    .btn-new:hover { background: #5a9aef; }
    .btn-send {
      width: 44px;
      height: 44px;
      border-radius: 50%;
      padding: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      background: var(--accent, #6ea8fe); color: #fff;
    }
    .btn-send:hover { background: #5a9aef; }
    .btn-send:disabled { opacity: 0.5; }
    .empty { display: flex; align-items: center; justify-content: center; height: 100%; color: var(--muted, #8b8d93); font-size: 14px; flex-direction: column; gap: 8px; }
    .session-select { padding: 6px 10px; border-radius: 6px; border: 1px solid var(--border, #2e2f33); background: var(--bg-surface, #222326); color: var(--fg, #e4e5e7); font-size: 12px; }
    .typing { font-style: italic; color: var(--muted, #8b8d93); font-size: 13px; padding: 4px 0; }
    .chat-header { gap: 16px; display: flex; align-items: center; justify-content: space-between; }
    .chat-header-left { display: flex; flex-direction: column; gap: 4px; }
    .btn-new { background: var(--accent, #6ea8fe); color: white; border-color: var(--accent, #6ea8fe); }
    .btn-new:hover { background: #5a9aef; }
    .empty-hint { font-size: 12px; color: var(--muted, #8b8d93); margin-top: 8px; line-height: 1.5; }
    .example-questions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 16px; justify-content: center; }
    .example-btn { padding: 6px 12px; border-radius: 6px; border: 1px solid var(--border, #2e2f33); background: transparent; color: var(--accent, #6ea8fe); font-size: 11px; cursor: pointer; transition: all 0.2s; }
    .example-btn:hover { background: rgba(110,168, 254, 0.1); }
  `;

  @state() private _messages: ChatMessage[] = [];
  @state() private _loading = true;
  @state() private _sending = false;
  @state() private _inputValue = "";
  @state() private _sessionKey = "general-chat";
  @state() private _pollTimer: ReturnType<typeof setInterval> | null = null;
  @state() private _sessionId = "";

  connectedCallback() {
    super.connectedCallback();
    this._initSession();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._pollTimer) clearInterval(this._pollTimer);
  }

  private async _initSession() {
    console.log('[Chat] Initializing session...');
    // Skip run session lookup - always use general chat
    this._loading = false;
    await this._createGeneralSession();
    // Start polling for messages
    this._pollTimer = setInterval(() => this._loadHistory(), 3000);
  }

  private async _loadHistory() {
    if (!this._sessionKey) {
      this._loading = false;
      return;
    }
    const result = await hermesClient.request<{ messages: ChatMessage[] }>("chat.history", {
      sessionKey: this._sessionKey,
      limit: 100,
    });
    if (result?.messages) {
      this._messages = result.messages;
    }
    this._loading = false;
  }

  private async _sendMessage() {
    const content = this._inputValue.trim();
    console.log('[Chat] Sending message:', content);
    if (!content) return;

    this._sending = true;
    this._inputValue = "";

    // Add user message immediately
    const userMessage: ChatMessage = {
      role: "user",
      content,
      created_at: Date.now(),
      label: "You"
    };
    this._messages = [...this._messages, userMessage];
    this.requestUpdate();

    try {
      const response = await hermesClient.request("chat.send", {
        sessionKey: this._sessionKey,
        content,
        deliver: true,
      });
      console.log('[Chat] Send response:', response);
    } catch (error) {
      console.error('[Chat] Send error:', error);
      // Add error message
      const errorMessage: ChatMessage = {
        role: "system",
        content: `Error: ${error instanceof Error ? error.message : 'Failed to send message'}`,
        created_at: Date.now(),
        label: "System"
      };
      this._messages = [...this._messages, errorMessage];
    }

    this._sending = false;
    await this._loadHistory();
  }

  private async _createGeneralSession() {
    try {
      const result = await hermesClient.request<{
        session_id: string;
        session_key: string;
      }>("sessions.create", {
        key: this._sessionKey,
        label: "General Chat"
      });

      if (result?.session_id) {
        this._sessionId = result.session_id;
        console.log('Created general chat session:', result);
        // Start polling for messages
        this._pollTimer = setInterval(() => this._loadHistory(), 3000);
      }
    } catch (error) {
      console.error('Failed to create session:', error);
      // Add a system message to inform the user
      const errorMsg: ChatMessage = {
        role: "system",
        content: "Chat session created. You can now send messages!",
        created_at: Date.now(),
        label: "System"
      };
      this._messages = [errorMsg];
    }
  }

  render() {
    return html`
      <div class="chat-container">
        <div class="chat-header">
          <div>
            <h2>💬 Chat</h2>
            ${this._sessionId ? html`<span style="font-size: 11px; color: var(--muted);">Session: ${this._sessionId.slice(0, 8)}...</span>` : ""}
          </div>
          <button class="btn" @click=${() => this._createNewSession()}>+ New Chat</button>
        </div>
        ${this._loading
          ? html`<div class="empty">Loading chat...</div>`
          : html`
            <div class="messages">
              ${this._messages.length === 0
                ? html`<div class="empty">
                    <div style="font-size: 32px; margin-bottom: 12px;">💬</div>
                    <div style="font-size: 16px; font-weight: 500; margin-bottom: 8px;">Start a conversation</div>
                    <div style="font-size: 13px; color: var(--muted);">Type your message below and press Enter</div>
                  </div>`
                : this._messages.map((m) => this._renderMessage(m))
              }
              ${this._sending ? html`<div class="typing">🤔 Agent is thinking...</div>` : ""}
            </div>
            <div class="input-bar">
              <input
                type="text"
                id="chatInput"
                placeholder="Type your message here..."
                .value=${this._inputValue}
                @input=${this._handleInput}
                @keydown=${this._handleKeyDown}
                autofocus
              />
              <button class="btn btn-send" ?disabled=${this._sending || !this._inputValue.trim()} @click=${this._sendMessage}>
                ${this._sending ? '...' : '➤'}
              </button>
            </div>
          `}
      </div>
    `;
  }

  private _handleInput(e: Event) {
    const target = e.target as HTMLInputElement;
    console.log('[Chat] Input event:', target.value);
    this._inputValue = target.value;
  }

  private _handleKeyDown(e: KeyboardEvent) {
    console.log('[Chat] Keydown:', e.key, 'Value:', this._inputValue);
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this._sendMessage();
    }
  }

  private _createNewSession() {
    this._messages = [];
    this._sessionId = "";
    this._sessionKey = `general-chat-${Date.now()}`;
    this._createGeneralSession();
  }

  private _renderMessage(m: ChatMessage) {
    const role = m.role || "user";
    const avatar = role === "assistant" ? "AI" : role === "system" ? "SYS" : "U";
    const label = m.label || role;
    const ts = m.created_at ? formatMs(m.created_at) : "";
    return html`
      <div class="msg">
        <div class="msg-avatar ${role}">${avatar}</div>
        <div class="msg-body">
          <div class="msg-meta">${label} · ${ts}</div>
          <div class="msg-content" .innerHTML=${toSanitizedMarkdownHtml(m.content || "")}></div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-chat-view": EadChatView;
  }
}
